import React, { useRef } from 'react';
import { Transition } from 'react-transition-group';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';

import knockingSvg from '../assets/knocking-hand.svg';
import { PrimaryButton, NavButtonGroup } from '../components/buttons';
import Header from '../components/Header';
import {
  pageTransitionDurationMs,
  transitionStylesCrossFadeIn,
  transitionStylesCrossFadeOut,
} from '../components/transitions';
import { BodyExtraLarge, BodyMedium, BodySmall } from '../components/typography';
import ZoneSelector from '../components/ZoneSelector';
import useHotspotHelper from '../hooks/useHotspotHelper';

export default function HomeExterior ({ dispatch, state }) {
  const { currentScreen, prevScreen, selectedHome, SCREENS } = state;
  const image = selectedHome?.exteriorImages?.[0];
  const metadata = `${selectedHome.beds} bd • ${selectedHome.baths} ba • ${selectedHome.sqFt} ft`;

  // We need to manually track a scale factor to apply to the hotspot locations.
  // This is because the browser will scale and clip the image in order to both
  // preserve the image aspect ratio AND cover the entire viewport.
  const { computeScaleFactor, imgRef, imgSize, scaleFactor } = useHotspotHelper();

  const nodeRef = useRef(null);
  return (
    <Transition
      in={currentScreen === SCREENS.HOME_2D}
      mountOnEnter
      nodeRef={nodeRef}
      timeout={pageTransitionDurationMs}
      unmountOnExit
    >
      {transitionState => {
        const transitionStyles = (
          (currentScreen === SCREENS.COMMUNITY_OVERVIEW && prevScreen === SCREENS.HOME_2D) ||
          (currentScreen === SCREENS.HOME_2D && prevScreen === SCREENS.COMMUNITY_OVERVIEW)
        ) ? transitionStylesCrossFadeIn : transitionStylesCrossFadeOut;
        return (
          <Box
            ref={nodeRef}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              height: '100%',
              width: '100%',
              transition: `all ${pageTransitionDurationMs}ms ease-in-out`,
              ...transitionStyles['exited'],
              ...transitionStyles[transitionState],
            }}
          > 
            <img
              alt={image?.name || ''}
              onLoad={computeScaleFactor}
              ref={imgRef}
              src={image?.url || ''} 
              style={{
                position: 'fixed',
                objectFit: 'cover',
                objectPosition: 'center center',
                height: '100%',
                width: '100%',
                zIndex: -1
              }}
              height={image?.height || undefined}
              width={image?.width || undefined}
            />

            <Header dispatch={dispatch} state={state} />

            <Stack
              direction='column'
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                pb: 4.5,
                pl: 4,
              }}
            >
              <BodySmall sx={{ fontWeight: 500, mb: 0.25, textTransform: 'uppercase' }}>
                  {selectedHome.street}
              </BodySmall>
              <BodySmall sx={{ fontWeight: 500, mb: 2 }}>
                {metadata}
              </BodySmall>
              <BodyExtraLarge>
                {(selectedHome.availability === 'SOLD') ? 'SOLD' : selectedHome.price}
              </BodyExtraLarge>
              <BodyMedium
                sx={{
                  fontFamily: 'Reckless',
                  mb: 3
                }}
              >
                {selectedHome.payment}
              </BodyMedium>
              <PrimaryButton
                label='Reserve Now'
                onClick={() => dispatch({ type: 'reserve-now-button-clicked' })}
                size='large'
              />
            </Stack>
            
            <Stack
              direction='row'
              spacing={1.5}
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                pb: 4.5,
                pr: 2,
              }}
            >
              <ZoneSelector
                dispatch={dispatch}
                state={state}
              />
              <NavButtonGroup
                dispatch={dispatch}
                state={state}
              />
            </Stack>

            {scaleFactor > 0 && image?.x && image?.y && (
              <IconButton
                onClick={() => dispatch({ type: 'enter-home-button-clicked' })}
                sx={{
                  position: 'absolute',
                  top: `calc(50% + ${(image.y - 0.5) * imgSize.height * scaleFactor}px)`,
                  left: `calc(50% + ${(image.x - 0.5) * imgSize.width * scaleFactor}px)`,
                  transform: 'translate(-50%, -50%)',
                  height: '3.5rem',
                  width: '3.5rem',
                  zIndex: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(45, 45, 45, 0.4)',
                  }
                }}
              >
                <img alt='Enter Home' src={knockingSvg} /> 
              </IconButton>
            )}
          </Box>
        );
      }}
    </Transition>
  );
}
